/**
 * L3's scenario bank — plan §4.6's twenty-four items, verbatim.
 *
 * 🔴 CORRECT-ANSWER POSITION IS RANDOMISED AT RUNTIME, NEVER AUTHORED. §4.5 is a
 * standing platform rule: *"or children learn positional habits instead of
 * content."* The `options` order below is the order they are WRITTEN, not the
 * order they are shown — `sampleScenario` shuffles with an injected source, the
 * same convention `sampleRound` uses across this folder.
 *
 * ⚠️ CONFUSION PAIRS MAY NOT BE CO-PRESENT BELOW TIER 3 (rules 16–19). §4.7:
 * `good_evening` ↔ `good_night`, `good_afternoon` ↔ `good_morning`,
 * `sorry` ↔ `excuse_me`. A Tier 1 item that offers both halves of a pair is not
 * testing the child's knowledge, it is testing a coin flip — and the tier
 * structure exists so the near-miss is the LAST thing they meet, not the first.
 */

export interface L3Scenario {
  id: string;
  tier: 1 | 2 | 3;
  /** The situation, read aloud and carried by the card. */
  situation: string;
  /** Written order only — the runtime shuffles. */
  options: readonly string[];
  answer: string;
  /** The scenario card that carries the situation. */
  card: string;
  /** Tier 3 only — why this one is hard, for the reviewer package. */
  why?: string;
}

/* ───────────────────────────────── Tier 1 — two options ── */

const TIER_1: readonly L3Scenario[] = [
  {
    id: "l3-t1-01",
    tier: 1,
    situation: "You come to school in the morning. You see your teacher.",
    options: ["Good morning", "Good night"],
    answer: "Good morning",
    card: "card_school_gate_morning",
  },
  {
    id: "l3-t1-02",
    tier: 1,
    situation: "Amma gives you a biscuit.",
    options: ["Thank you", "Sorry"],
    answer: "Thank you",
    card: "card_biscuit",
  },
  {
    id: "l3-t1-03",
    tier: 1,
    situation: "Your friend is going home.",
    options: ["Bye", "Hello"],
    answer: "Bye",
    card: "card_friend_leaving",
  },
  {
    id: "l3-t1-04",
    tier: 1,
    situation: "It's dark. You are getting into bed.",
    options: ["Good night", "Good morning"],
    answer: "Good night",
    card: "card_bedtime",
  },
  {
    id: "l3-t1-05",
    tier: 1,
    situation: "You bump into someone by mistake.",
    options: ["Sorry. Are you okay?", "Thank you"],
    answer: "Sorry. Are you okay?",
    card: "card_bump",
  },
  {
    id: "l3-t1-06",
    tier: 1,
    situation: "Your friend comes to your house.",
    options: ["Hello", "Bye"],
    answer: "Hello",
    card: "card_friend_arrives",
  },
  {
    id: "l3-t1-07",
    tier: 1,
    situation: "Nani gives you a gift.",
    options: ["Thank you", "Excuse me"],
    answer: "Thank you",
    card: "card_gift",
  },
  /**
   * 🔴 WHO YOU ARE GREETING CHANGES THE GREETING, AND THE BANK DID NOT TEACH IT.
   * §4.6 covered WHEN (morning/evening) and WHAT FOR (thank you/sorry) but never
   * WHO — yet "namaste to an elder, hi to a friend" is the distinction an Indian
   * four-year-old is corrected on most often at home.
   *
   * ⚠️ THE PAIR IS DELIBERATE AND MUST STAY TOGETHER. Taught alone, "namaste to
   * grown-ups" becomes "namaste to everyone" and the child greets their friend
   * like a stranger; "hi to friends" alone becomes "hi to everyone" and the
   * elder is greeted casually. The contrast IS the lesson, which is why both are
   * Tier 1 and adjacent.
   *
   * ⚠️ §4.3 ALREADY MAKES NAMASTE FIRST-CLASS — *"it works for hello AND
   * goodbye, morning AND night, with anyone. It's the one greeting that's never
   * wrong."* So neither of these can mark namaste WRONG for a friend; the friend
   * item offers Hi against clearly-wrong alternatives instead.
   */
  {
    id: "l3-t1-09",
    tier: 1,
    situation: "Your uncle comes to visit. He is a grown-up.",
    options: ["Namaste", "Bye"],
    answer: "Namaste",
    card: "card_evening_home",
  },
  {
    id: "l3-t1-10",
    tier: 1,
    situation: "You see your friend at the door. Your friend is the same age as you.",
    options: ["Hi", "Good night"],
    answer: "Hi",
    card: "card_friend_arrives",
  },
  {
    id: "l3-t1-08",
    tier: 1,
    situation: "You want to walk past your friend in the doorway.",
    options: ["Excuse me", "Good night"],
    answer: "Excuse me",
    card: "card_doorway",
  },
];

/* ─────────────────────────────── Tier 2 — three options ── */

const TIER_2: readonly L3Scenario[] = [
  {
    id: "l3-t2-01",
    tier: 2,
    situation: "Sun is high. You see the lunch aunty at school.",
    options: ["Good afternoon", "Good night", "Bye"],
    answer: "Good afternoon",
    card: "card_school_gate_afternoon",
  },
  {
    id: "l3-t2-02",
    tier: 2,
    situation: "Sun is going down. Nana comes home from work.",
    options: ["Good evening", "Good morning", "Sorry"],
    answer: "Good evening",
    card: "card_evening_home",
  },
  {
    id: "l3-t2-03",
    tier: 2,
    situation: "You spilled water on your friend's drawing.",
    options: ["Sorry. Are you okay?", "Thank you", "Hello"],
    answer: "Sorry. Are you okay?",
    card: "card_spilled_drawing",
  },
  {
    id: "l3-t2-04",
    tier: 2,
    situation: "Someone offers you a sweet. You don't want it.",
    options: ["No, thank you", "Sorry", "Bye"],
    answer: "No, thank you",
    card: "card_offered_sweet",
  },
  {
    id: "l3-t2-05",
    tier: 2,
    situation: "You want the crayon your friend is holding.",
    options: ["Please can I have it?", "Excuse me", "Thank you"],
    answer: "Please can I have it?",
    card: "card_crayon",
  },
  {
    id: "l3-t2-06",
    tier: 2,
    situation: "You meet your grandmother. You want to be respectful.",
    options: ["Namaste", "Bye", "Good night"],
    answer: "Namaste",
    card: "card_grandmother",
  },
  /**
   * The elder/friend contrast again at Tier 2, now with THREE options and the
   * two greetings side by side — which Tier 1 deliberately avoids.
   */
  {
    id: "l3-t2-11",
    tier: 2,
    situation: "Your father's friend comes to the house. He is a grown-up.",
    options: ["Namaste", "Hi", "Good night"],
    answer: "Namaste",
    card: "card_evening_home",
  },
  {
    id: "l3-t2-12",
    tier: 2,
    situation: "Your friend is waiting for you to play.",
    options: ["Hi", "Good evening", "Sorry"],
    answer: "Hi",
    card: "card_crayon",
  },
  {
    id: "l3-t2-07",
    tier: 2,
    situation: "Two grown-ups are talking. You need to say something.",
    options: ["Excuse me", "Sorry", "Hello"],
    answer: "Excuse me",
    card: "card_two_adults_talking",
  },
  {
    id: "l3-t2-08",
    tier: 2,
    situation: "You wake up. Amma is in the kitchen.",
    options: ["Good morning", "Good evening", "Bye"],
    answer: "Good morning",
    card: "card_school_gate_morning",
  },
  {
    id: "l3-t2-09",
    tier: 2,
    situation: "Your teacher helped you tie your shoe.",
    options: ["Thank you", "Please", "Excuse me"],
    answer: "Thank you",
    card: "card_shoelace",
  },
  {
    id: "l3-t2-10",
    tier: 2,
    situation: "Friend leaving after playing all day. It's getting dark.",
    options: ["Bye, good evening", "Good morning", "Hello"],
    answer: "Bye, good evening",
    card: "card_friend_leaving",
  },
];

/* ────────────────────── Tier 3 — near-miss distractors ── */

const TIER_3: readonly L3Scenario[] = [
  {
    id: "l3-t3-01",
    tier: 3,
    situation: "It's evening. You're still awake. You see your uncle.",
    options: ["Good evening", "Good night", "Good afternoon"],
    answer: "Good evening",
    card: "card_evening_home",
    why: "The level's core confusion",
  },
  {
    id: "l3-t3-02",
    tier: 3,
    situation: "It's night. Leaving Nani's house to go home to sleep.",
    options: ["Good night", "Good evening", "Hello"],
    answer: "Good night",
    card: "card_bedtime",
    why: "Night as a farewell, not a greeting",
  },
  {
    id: "l3-t3-03",
    tier: 3,
    situation: "You stepped on someone's foot AND need to get past.",
    options: ["Sorry, excuse me", "Thank you", "Namaste"],
    answer: "Sorry, excuse me",
    card: "card_bump",
    why: "Both words, right order",
  },
  {
    /**
     * 🔴 LOAD-BEARING — VALIDATOR RULE 3, AND THE ONE ITEM THIS BANK EXISTS TO
     * PROTECT. Plan §4.6: *"'Sorry' is the distractor. If a pilot shows children
     * reliably picking it, that is a FINDING, not a scoring blip — it means the
     * courtesy content is overwriting the autonomy content and the levels need
     * re-separating."*
     *
     * ⚠️ Its card must read as completely ordinary — asset pack §6 says any
     * tension at all turns it into a body-safety scenario, which is a different
     * level with different rules, and the answer stops being about courtesy.
     */
    id: "l3-t3-04",
    tier: 3,
    situation: "Someone wants a hug. You don't want one right now.",
    options: ["No, thank you", "Sorry", "Please"],
    answer: "No, thank you",
    card: "card_offered_sweet",
    why: "Tests §1 rule 1 — courtesy must never answer a boundary",
  },
  {
    id: "l3-t3-05",
    tier: 3,
    situation: "Just after lunch. Your teacher walks in.",
    options: ["Good afternoon", "Good morning", "Good evening"],
    answer: "Good afternoon",
    card: "card_school_gate_afternoon",
    why: "Least-known of the four",
  },
  {
    id: "l3-t3-06",
    tier: 3,
    situation: "Your friend gave you their toy to keep.",
    options: ["Thank you so much", "Sorry", "Excuse me"],
    answer: "Thank you so much",
    card: "card_gift",
    why: "Intensity matching",
  },
];

export const L3_SCENARIOS: readonly L3Scenario[] = [
  ...TIER_1,
  ...TIER_2,
  ...TIER_3,
];

/**
 * Pick a scenario's options in display order.
 *
 * 🔴 THE SHUFFLE IS HERE, NOT IN THE DATA, AND THE SOURCE IS INJECTED. Authoring
 * a shuffled order would freeze one arrangement forever, which is the positional
 * habit §4.5 forbids; shuffling with `Math.random` inline would make the bank
 * untestable without a seed.
 */
export function optionsInOrder(
  s: L3Scenario,
  rand: () => number = Math.random,
): readonly string[] {
  const out = [...s.options];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
